/**
 * careerPathways.utils.js
 *
 * Pure helpers behind the employee Career Pathways screen — no React — so
 * the track position-selection and saved-aspiration-hydration rules can be
 * unit-tested apart from the component tree.
 *
 * Contract for a track's `positions` (POST /career-paths — see the backend's
 * career_paths_service): the array starts parallel to `roles`, in path order
 * with the employee's current role first — but positions whose JD is marked
 * non-discoverable are DROPPED from the array entirely, not flagged. So the
 * array is NOT index-stable: the current role's own position may be absent,
 * and any intermediate step may be missing. The response's top-level
 * `current_jd_sb_unique_id` is the only reliable way to tell which surviving
 * position is the current role.
 */

import { isSelectAll } from "Components/SbJdCreationDrawerV2/helpers";

// Survivors always arrive with is_discoverable: true today (dropped rows
// never reach the response) — the filter stays as a cheap guard against an
// older serializer that flags instead of drops.
export const getDiscoverablePositions = (track) =>
  (track?.positions || []).filter((position) => position.is_discoverable !== false);

/**
 * The track's "next step up" opening: the first position along the path that
 * is NOT the employee's current role. Index arithmetic (positions[1]) is
 * wrong here — a dropped current-role position shifts everything left, so
 * index 1 could be two steps out and index 0 could already be the next step;
 * and when only the current role survives there is no next-step opening at
 * all. Without a currentJdId to compare against (degraded response), fall
 * back to the old positional guess.
 */
export const findNextStepPosition = (positions, currentJdId) => {
  if (currentJdId == null) {
    return positions.length === 1 ? positions[0] : positions[1] ?? null;
  }
  return positions.find((position) => position.jd_sb_unique_id !== currentJdId) ?? null;
};

/**
 * Which of a track's discoverable positions its card lists — OR across the
 * reasons the track is showing: matched via the department checkbox → that
 * department's openings; matched via a tag checkbox, or no boxes checked →
 * the track's next-step opening. A track matching both shows both sets, in
 * path order.
 *
 * The department match excludes the employee's own current role (by
 * currentJdId, not index — see this file's header) even though
 * trackMatchesFilter's track-level check deliberately counts it (a track
 * the employee is already standing in should still show up in the filtered
 * list). At the position level, though, it's never something to apply
 * to — without this exclusion, checking the department checkbox alone
 * would surface the current role itself as an "open position" card.
 */
export const selectTrackPositions = ({
  positions,
  currentJdId,
  showDepartmentPositions,
  currentDepartment,
  showNextStep,
}) => {
  const nextStep = showNextStep ? findNextStepPosition(positions, currentJdId) : null;
  return positions.filter(
    (position) =>
      (showDepartmentPositions &&
        position.jd_sb_unique_id !== currentJdId &&
        position.department === currentDepartment) ||
      position === nextStep
  );
};

/**
 * Positions ranked best-match-first for display — SMI descending. A null
 * smi_score (not yet scored — is_smi_loading true, or the employee hasn't
 * updated their profile) floors to -1 rather than 0, the same convention
 * RedeploymentCenterV2.utils' buildScoreColumn default sorter uses, so an
 * unscored position sinks to the bottom instead of outranking (or tying)
 * a real, if low, match.
 *
 * Kept separate from selectTrackPositions above, which decides WHICH
 * positions a track shows (in path order, for its own reasons) — this only
 * reorders that result for display, so selectTrackPositions' own "path
 * order" contract stays accurate for any other caller.
 */
export const sortPositionsBySmi = (positions) =>
  [...positions].sort((a, b) => (b.smi_score ?? -1) - (a.smi_score ?? -1));

/**
 * Maps POST /career-paths' empty-payload rehydration response (see the
 * backend's career_paths_service.get_saved_career_paths) into
 * AspirationCaptureV2's initialAspiration shape.
 *
 * target_role_id / target_role_title are always arrays on the wire, e.g.:
 *   { target_role_id: ["bHTva5TrWHapzys"], target_role_title: ["Chief Technology Officer"] }
 * — a saved target_role_name can resolve to several published JDs sharing
 * it (one per business unit, say), so the backend stores/returns the full
 * group rather than a single id. Every entry in target_role_title is
 * identical regardless of group size (one target_role_name resolved the
 * whole group), and a saved aspiration only ever has one target_role_name —
 * so this takes the group's first id/title as the single value
 * AspirationCaptureV2's Target Role Select needs, the same "one id
 * represents its title" collapsing the capture form's own roleOptions
 * already do for duplicate postings.
 *
 * Left as the raw array, it never matches any option's plain-string value
 * once the published-jobs list loads (a single-element array's own
 * toString() happens to equal its one element, which is why the Target Role
 * field looked like it "sometimes" rendered a bare id instead of the title
 * while jobs were still loading), and then gets silently cleared by
 * AspirationCaptureV2's own dropped-from-list effect once they have.
 *
 * relocate isn't persisted server-side — a saved aspiration always carries
 * an explicit location list (the backend expands an omitted one to every
 * location), so every hydrated aspiration is treated as relocating with
 * that list. KNOWN GAP: a saved "No relocation" answer therefore comes back
 * as "relocating with every location", and an Edit + re-save would persist
 * that flip — only persisting/returning `relocate` server-side can fix it.
 *
 * currentFunction isn't persisted either — callers fall back to looking the
 * current JD up among the tracks' positions instead (see
 * TalentMobilityEmployeeV2's currentDepartment).
 *
 * businessUnit/functionDepartment/subFunction mirror the request payload's
 * business_unit/function/sub_function under the same name flip
 * target_role_name → target_role_id/target_role_title uses — echoed back
 * as-is (like locations) once the backend persists and returns them, so
 * useOrgPlacement's cascade (keyed on those exact field names) can rehydrate
 * on reload instead of starting cleared. Until the backend adds these to its
 * response this is a no-op (fields are simply absent → null), same as any
 * other saved-aspiration field the backend hasn't shipped yet.
 */
export const mapSavedCareerPathsToAspiration = (savedCareerPaths) => ({
  targetRoleId: savedCareerPaths.target_role_id?.[0] ?? null,
  targetRoleTitle: savedCareerPaths.target_role_title?.[0] ?? null,
  mobile: true,
  locations: savedCareerPaths.locations || [],
  currentFunction: null,
  businessUnit: savedCareerPaths.business_unit ?? null,
  functionDepartment: savedCareerPaths.function ?? null,
  subFunction: savedCareerPaths.sub_function ?? null,
});

/**
 * One org-placement dropdown value (Business Unit / Function / Sub-function)
 * as a POST /career-paths filter. The org tree's "ALL - All" sentinel means
 * "don't narrow by this level" — the backend expresses that as a null/omitted
 * filter, and would otherwise try to match the literal sentinel text against
 * job_desc and find nothing. The Target Role dropdown already treats the
 * sentinel as "no filter" (placementMatchesJob), so the payload now agrees
 * with it.
 */
export const toCareerPathPlacementFilter = (value) => (isSelectAll(value) ? null : value ?? null);
